/* eslint-disable @typescript-eslint/ban-types */
import type { FormEvent, RefObject } from 'react';
import React, { useRef, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FormContext } from './FormContext';

interface IProps {
    children?: React.ReactNode;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSubmit?: (event: FormEvent<HTMLFormElement>, values: any) => void;
    className?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    values?: any;
    ref?: RefObject<HTMLFormElement>;

    setValue?: <T>(value: T) => void;
    disabled?: boolean;
}

export const Form: React.FC<IProps> = ({
    children,
    onSubmit: onSubmitProp,
    className,
    values: initialValues,
    ref,
    disabled,
}) => {
    const { i18n } = useTranslation();

    const formRef = useRef<HTMLFormElement>(null);
    const [formHasErrors, setFormHasErrors] = useState(false);

    // Use refs to store values and errors without triggering re-renders
    const validationErrorsRef = useRef<Record<string, boolean>>({});
    const valuesRef = useRef({ ...(initialValues || {}) });

    const updateFormHasErrors = useCallback(() => {
        const hasErrors = Object.keys(validationErrorsRef.current).length !== 0;
        setFormHasErrors(hasErrors);
    }, []);

    const addError = useCallback(
        (prop: string) => {
            validationErrorsRef.current[prop] = true;
            updateFormHasErrors();
        },
        [updateFormHasErrors],
    );

    const removeError = useCallback(
        (prop: string) => {
            if (validationErrorsRef.current[prop]) {
                delete validationErrorsRef.current[prop];
            }
            updateFormHasErrors();
        },
        [updateFormHasErrors],
    );

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const setValue = useCallback((key: string, value: any) => {
        valuesRef.current[key] = value;
    }, []);

    const handleSubmit = useCallback(() => {
        if (!disabled && onSubmitProp) {
            // Create a synthetic event to satisfy the onSubmit signature
            const syntheticEvent = {
                preventDefault: () => {},
            } as FormEvent<HTMLFormElement>;
            onSubmitProp(syntheticEvent, valuesRef.current);
        }
    }, [disabled, onSubmitProp]);

    const onSubmit = useCallback(
        (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            handleSubmit();
        },
        [handleSubmit],
    );

    const submit = useCallback(() => {
        handleSubmit();
    }, [handleSubmit]);

    const contextValue = useMemo(
        () => ({
            addError,
            removeError,
            formHasErrors,
            setValue,
            submit,
            lang: i18n.language,
            formRef,
            values: valuesRef.current,
            formDisabled: disabled || false,
        }),
        [
            addError,
            removeError,
            formHasErrors,
            setValue,
            submit,
            i18n.language,
            disabled,
        ],
    );

    return (
        <FormContext.Provider value={contextValue}>
            <form
                className={className}
                onSubmit={onSubmit}
                ref={ref || formRef}
            >
                {children}
            </form>
        </FormContext.Provider>
    );
};

Form.displayName = 'Form';
